const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import AutoHeight from 'embla-carousel-auto-height';
import './carousel-base.css';
import './carousel.css';
import { getButtonClasses } from '../../../config/colors';


// Utility to derive a friendly class from the color value & label
const colorToClass = (value, label) => {
  if (!value) return '';
  const varMatch = /^var\(--([^)]+)\)/.exec(value);
  if (varMatch) return `color-var-${varMatch[1].toLowerCase()}`;
  if (value.startsWith('#')) return `color-${value.slice(1).toLowerCase()}`;
  if (label) return `color-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return '';
};

const EmblaCarousel = ({ data, id }) => {
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.search?.subrequests?.[id]);

  const getImageUrl = (item) => {
    if (item.image_scales && item.image_field) {
      return `${item['@id']}/@@images/${item.image_field}`;
    }
    if (item.preview_image) return `${item.preview_image['@id']}/@@images/image`;
    if (item.lead_image) return `${item.lead_image['@id']}/@@images/image`;
    if (item.image) {
      const image = Array.isArray(item.image) ? item.image[0] : item.image;
      return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
    }
    return '';
  };

  const getListingImageUrl = (item) => {
    if (item['@type'] === 'Image') return `${item['@id']}/@@images/image`;
    return getImageUrl(item);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch (e) { return dateString; }
  };

  const renderDate = (slide) => {
    const mode = data.dateDisplay;
    if (!mode || mode === 'none') return null;
    if (mode === 'effective') {
      if (!slide.effectiveDate) return null;
      return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
    }
    if (mode === 'start') {
      if (slide.startDate) {
        return <p className="slide-date">Starts {formatDate(slide.startDate)}</p>;
      }
      if (slide.effectiveDate) {
        return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
      }
      return null;
    }
    return null;
  };

  const convertItemToSlide = (item) => ({
    heading: item.title || '',
    content: item.description || '',
    image: null,
    link: item,
    buttonText: data.listingButtonText || 'Read more',
    effectiveDate: item.effective || null,
    startDate: item.start || null,
    subjects: item.Subject || [],
    isFromListing: true,
  });

  const getSlides = () => {
    let slides = [];
    if (data.useListing && searchResults?.items && searchResults.items.length > 0) {
      slides = searchResults.items.map(convertItemToSlide);
    }
    if (!data.useListing || (data.useListing && data.appendManualSlides)) {
      const manualSlides = (data.slides || []).map(slide => ({
        ...slide,
        isFromListing: false
      }));
      slides = slides.concat(manualSlides);
    }
    return slides;
  };

  // ---- Tag filter state ----
  const parsedFilterTags = React.useMemo(() => {
    if (!data.filterTags) return [];
    return data.filterTags.split(',').map((t) => t.trim()).filter(Boolean);
  }, [data.filterTags]);

  const [activeTag, setActiveTag] = useState('all');

  // Reset to 'all' if tags change
  useEffect(() => {
    setActiveTag('all');
  }, [data.filterTags]);

  const allSlides = getSlides();

  // Determine which tags actually have matching slides
  const tagsWithSlides = React.useMemo(() => {
    return parsedFilterTags.filter((tag) =>
      allSlides.some((slide) => {
        const subjects = slide.subjects || slide.link?.Subject || [];
        return Array.isArray(subjects)
          ? subjects.includes(tag)
          : subjects === tag;
      }),
    );
  }, [parsedFilterTags, allSlides]);

  const slides = React.useMemo(() => {
    if (!parsedFilterTags.length || activeTag === 'all') return allSlides;
    return allSlides.filter((slide) => {
      const subjects = slide.subjects || slide.link?.Subject || [];
      return Array.isArray(subjects)
        ? subjects.includes(activeTag)
        : subjects === activeTag;
    });
  }, [allSlides, activeTag, parsedFilterTags]);

  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data.minSlidesOnMobile, 10) || 1;

  const calculateSlidesToShow = React.useCallback(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    if (w <= 540) return Math.max(1, minSlidesOnMobile);
    if (w <= 768) return Math.min(slidesToShow, Math.max(1, minSlidesOnMobile));
    return slidesToShow;
  }, [slidesToShow, minSlidesOnMobile]);

  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(calculateSlidesToShow);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      setEffectiveSlidesToShow(calculateSlidesToShow());
      setIsMobile(window.innerWidth <= 768);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [calculateSlidesToShow]);

  // Peek disabled on mobile and when fewer than 5 slides are visible
  const peekActive = data.centeredPeek && !isMobile && slides.length >= 5;

  useEffect(() => {
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: [
          'title', 
          'description', 
          'image_field',
          'image_scales',
          'effective',
          'start',
          'remoteUrl',
          'Subject',
        ],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          const transformedQuery = {};
          data.query.query.forEach(criterion => {
            if (criterion.i === 'portal_type') {
              transformedQuery.portal_type = criterion.v;
            } else if (criterion.i === 'path') {
              transformedQuery.path = criterion.v;
            } else if (criterion.i === 'Subject') {
              transformedQuery.Subject = criterion.v;
            } else if (criterion.i === 'review_state') {
              transformedQuery.review_state = criterion.v;
            }
          });
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order) transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit) transformedQuery.b_size = parseInt(data.query.limit, 10);
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit) searchOptions.b_size = parseInt(data.query.limit, 10);
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = baseOptions;
      } else {
        searchOptions = { ...baseOptions, query: data.query };
      }

      dispatch(searchContent(searchTerm, searchOptions, id));
    }
  }, [data.useListing, data.query, dispatch, id]);

  const carouselType = data.carouselType || 'default';

  const configOverrides = {
    default: { align: 'start', loop: data.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };

  const presetConfig = getPresetConfig(data.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [panelHeight, setPanelHeight] = useState(null);
  const panelSlideRefs = useRef([]);
  const panelImagesLoaded = useRef(0);

  const _displayMode = data.displayMode || data.mode || 'full';
  const isImageTopPanel = _displayMode === 'image-top-panel';

  const plugins = [
    ...(data.autoplay ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 4000, stopOnInteraction: false })] : []),
    ...(data.autoHeight ? [AutoHeight()] : []),
  ];
  const [viewportRef, embla] = useEmblaCarousel({
    ...config,
    loop: data.loop ?? true,
    align: peekActive ? 'center' : 'start',
    slidesToScroll: (peekActive || isImageTopPanel) ? 1 : effectiveSlidesToShow,
    ...(peekActive ? { containScroll: false } : {}),
  }, plugins);

  const handlePanelImageLoad = useCallback(() => {
    panelImagesLoaded.current += 1;
    if (embla) embla.reInit();
  }, [embla]);

  

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    onSelect(); // set initial selected index
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      try { embla.off('select', onSelect); } catch (e) {}
      try { embla.off('reInit', onSelect); } catch (e) {}
    };
  }, [embla]);

  // Scroll back to start whenever active tag changes
  useEffect(() => {
    if (!embla) return;
    embla.reInit();
    embla.scrollTo(0, true);
    setSelectedIndex(0);
  }, [activeTag, embla]);

  // Measure active panel slide height for auto-height on the content panel
  useEffect(() => {
    if (!isImageTopPanel) return;
    const el = panelSlideRefs.current[selectedIndex];
    if (el) setPanelHeight(el.offsetHeight);
  }, [selectedIndex, isImageTopPanel]);

const getHref = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return '#';
    if (item['@type'] === 'Link' && item.remoteUrl) return item.remoteUrl;
    const url = item['@id'] || '';
    const isFile = item['@type'] === 'File';
    return url ? (isFile ? `${url}/@@download/file` : url) : '#';
  };

  const isLogoMarquee = _displayMode === 'logo-marquee';
  const variantClass = `type-${_displayMode}`;
  const isImageOnly = _displayMode === 'image-only';
  const isImageTopMode = _displayMode === 'image-top';

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons || _displayMode === 'image-only') return null;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    const { className, style } = getButtonClasses(data.slideButtonStyle, 'embla__slide-btn');
    return (
      <a
        href={href}
        className={className}
        style={style}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;
    const href = getHref(slide.link);
    if (!href || href === '#') return;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  const imageFor = (slide) => {
    return slide.isFromListing
      ? getListingImageUrl(slide.link) 
      : (() => {
          const image = Array.isArray(slide.image) ? slide.image[0] : slide.image;
          return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
        })();
  };

  const heightClass = data.equalHeight ? 'equal-height' : '';
  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const colorClass = colorToClass(data.backgroundColor);
  const toneClass = data.textTone === 'light' ? 'tone-light' : 'tone-dark';

  const showArrows = data.hideArrows !== true && slides.length > 1 && !isLogoMarquee;
  const showDots = data.hideDots !== true && slides.length > 0 && !isLogoMarquee;

  const pageCount = (peekActive || isImageTopPanel)
    ? slides.length
    : Math.ceil(slides.length / effectiveSlidesToShow);

  const blockId = data.title
    ? data.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    : undefined;

  return (
    <div
      id={blockId}
      className={`embla ${heightClass} ${data.isFullWidth ? 'full-width' : ''} ${peekActive ? 'centered-peek' : ''} align-${data.alignment || 'left'} ${modeClass} ${variantClass} ${toneClass} ${colorClass} ${data.outerClassName || ''}`}
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      <div className="embla__inner">

        {/* Logo marquee */}
        {isLogoMarquee ? (
          <>
            {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
            <div
              className={`logo-marquee ${data.pauseOnHover ? 'pause-on-hover' : ''}`}
              style={{
                '--marquee-duration': `${parseInt(data.marqueeSpeed, 10) || 20}s`,
                '--logo-height': `${parseInt(data.logoHeight, 10) || 48}px`,
                '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
                '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
              }}
            >
              <div className="logo-marquee__track">
                {[...slides, ...slides].map((slide, i) => {
                  const src = imageFor(slide);
                  if (!src) return null;
                  const alt = slide.heading || 'Logo';
                  const href = getHref(slide.link);
                  const clickable = data.clickableSlides && href && href !== '#';
                  const img = <img className="logo-marquee__img" src={src} alt={alt} />;
                  return (
                    <div className="logo-marquee__item" key={`r-${i}`}>
                      {clickable ? (
                        <a
                          href={href}
                          className="logo-marquee__link"
                          target="_blank"
                        >
                          {img}
                        </a>
                      ) : img}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header row — title/description left, arrows+button right when arrowPosition=top */}
            {(data.title || data.description || (showArrows && data.arrowPosition === 'top')) && (
              <div className={`embla__header ${data.arrowPosition === 'top' ? 'embla__header--has-controls' : ''}`}>
                <div className="embla__header-top">
                  {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
                  {showArrows && data.arrowPosition === 'top' && slides.length > 1 && (() => {
                    const arrowStyleParsed = getButtonClasses(data.arrowStyle, 'embla__arrow');
                    const linkStyleParsed = getButtonClasses(data.headerLinkStyle, 'embla__header-link');
                    return (
                      <div className="embla__header-controls embla__header-controls--desktop">
                        <button
                          className={`embla__prev ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollPrev()}
                        >
                          <i className="bi bi-arrow-left-short"></i>
                        </button>
                        {data.headerLinkText && (
                          <a
                            href={getHref(data.headerLinkUrl)}
                            className={linkStyleParsed.className}
                            style={linkStyleParsed.style}
                            {...(getHref(data.headerLinkUrl).startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >
                            {data.headerLinkText}
                          </a>
                        )}
                        <button
                          className={`embla__next ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollNext()}
                        >
                          <i className="bi bi-arrow-right-short"></i>
                        </button>
                      </div>
                    );
                  })()}
                </div>
                {data.description ? <p className="embla__description">{data.description}</p> : null}
                {showArrows && data.arrowPosition === 'top' && slides.length > 1 && (() => {
                  const arrowStyleParsed = getButtonClasses(data.arrowStyle, 'embla__arrow');
                  const linkStyleParsed = getButtonClasses(data.headerLinkStyle, 'embla__header-link');
                  return (
                    <div className="embla__header-controls embla__header-controls--mobile">
                      <button
                        className={`embla__prev ${arrowStyleParsed.className}`}
                        style={arrowStyleParsed.style}
                        onClick={() => embla && embla.scrollPrev()}
                      >
                        <i className="bi bi-arrow-left-short"></i>
                      </button>
                      {data.headerLinkText && (
                        <a
                          href={getHref(data.headerLinkUrl)}
                          className={linkStyleParsed.className}
                          style={linkStyleParsed.style}
                          {...(getHref(data.headerLinkUrl).startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {data.headerLinkText}
                        </a>
                      )}
                      <button
                        className={`embla__next ${arrowStyleParsed.className}`}
                        style={arrowStyleParsed.style}
                        onClick={() => embla && embla.scrollNext()}
                      >
                        <i className="bi bi-arrow-right-short"></i>
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Title/description standalone when no top controls */}
            {data.arrowPosition !== 'top' && !data.title && !data.description ? null : null}

            {/* Tag filter tabs */}
            {tagsWithSlides.length > 0 && (
              <div className="embla__filter-tabs" role="tablist" aria-label="Filter content">
                <button
                  role="tab"
                  aria-selected={activeTag === 'all'}
                  className={`embla__filter-tab ${activeTag === 'all' ? 'is-active' : ''}`}
                  onClick={() => setActiveTag('all')}
                >
                  All
                </button>
                {tagsWithSlides.map((tag) => (
                  <button
                    key={tag}
                    role="tab"
                    aria-selected={activeTag === tag}
                    className={`embla__filter-tab ${activeTag === tag ? 'is-active' : ''}`}
                    onClick={() => setActiveTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            <div className="embla__viewport" ref={viewportRef}>
              <div className="embla__container">
                {slides.map((slide, index) => {
                  const imageUrl = imageFor(slide);
                  const href = getHref(slide.link);
                  const isClickable = data.clickableSlides && href && href !== '#';

                  return (
                    <div className="embla__slide" key={index} style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}>
                      <div className="slide__content">
                      <div
                        className={`carousel-slide-inner ${isClickable ? 'clickable' : ''} ${isImageOnly ? 'image-only' : ''} ${isImageTopMode ? 'image-top' : ''} ${isImageTopPanel ? 'panel-slide' : ''} ${slide.isFromListing ? 'listing-slide' : 'manual-slide'}`}
                        style={{ 
                          backgroundImage: !isImageOnly && !isImageTopMode && imageUrl ? `url(${imageUrl})` : 'none',
                          cursor: isClickable ? 'pointer' : 'default'
                        }}
                        onClick={() => handleSlideClick(slide)}
                      >
                        {isImageOnly ? (
                          imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={slide.heading || 'Slide image'} 
                            />
                          ) : (
                            <div className="image-placeholder">
                              No image
                            </div>
                          )
                        ) : isImageTopPanel ? (
                          imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={slide.heading || 'Slide image'}
                              className="panel-slide__img"
                              onLoad={handlePanelImageLoad}
                            />
                          ) : (
                            <div className="image-placeholder">No image</div>
                          )
                        ) : isImageTopMode ? (
                          <div className="card card-image-top">
                            {imageUrl ? (
                              <div className="card-media">
                                <img src={imageUrl} alt={slide.heading || 'Slide image'} />
                              </div>
                            ) : null}
                            <div className="card-content">
                              {renderDate(slide)}
                              {slide.preheader && <p className="slide__preheader">{slide.preheader}</p>}
                              <h3>{slide.heading}</h3>
                              {!data.hideDescription && <p>{slide.content}</p>}
                              {renderLink(slide)}
                            </div>
                          </div>
                        ) : (
                          <div className="slide__body">
                            {renderDate(slide)}
                            {slide.preheader && <p className="slide__preheader">{slide.preheader}</p>}
                            <h3>{slide.heading}</h3>
                            {!data.hideDescription && <p>{slide.content}</p>}
                            {renderLink(slide)}
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {showArrows && slides.length > 1 && data.arrowPosition !== 'top' && (
              <div className="embla__controls" aria-hidden="false">
                <div className="embla__controls__buttons" aria-hidden="false">
                  <button className="embla__prev" onClick={() => embla && embla.scrollPrev()}><i className="bi bi-arrow-left-short"></i></button>
                  <button className="embla__next" onClick={() => embla && embla.scrollNext()}><i className="bi bi-arrow-right-short"></i></button>
                </div>
              </div>
            )}

            {showDots && pageCount > 1 && (
              <div className="embla__nav">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => embla && embla.scrollTo(i)}
                    className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
                  />
                ))}
              </div>
            )}

            {/* Shared content panel — image-top-panel mode only */}
            {isImageTopPanel && slides.length > 0 && (
              <div
                className="embla__panel"
                style={panelHeight ? { height: panelHeight } : undefined}
              >
                <div
                  className="embla__panel-track"
                  style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
                >
                  {slides.map((slide, index) => (
                    <div
                      className="embla__panel-slide"
                      key={index}
                      ref={el => { panelSlideRefs.current[index] = el; }}
                    >
                      {renderDate(slide)}
                      {slide.preheader && <p className="slide__preheader">{slide.preheader}</p>}
                      {slide.heading && <h3>{slide.heading}</h3>}
                      {!data.hideDescription && (
                        slide.richText?.data
                          ? <div dangerouslySetInnerHTML={{ __html: slide.richText.data }} />
                          : slide.content
                            ? <p>{slide.content}</p>
                            : null
                      )}
                      {renderLink(slide)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmblaCarousel;